import { BehaviorSubject, Subject, Observable } from "rxjs";
import { ConnectionState, ConnectionConfig } from "./types";

export class WebSocketConnection {
  private ws: WebSocket | null = null;
  private connectionId: string;
  private config: Required<ConnectionConfig>;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionTimeout: ReturnType<typeof setTimeout> | null = null;
  private messageSubject = new Subject<any>();
  private state$!: BehaviorSubject<ConnectionState>;

  constructor(
    connectionId: string,
    endpoint: string,
    config: Partial<ConnectionConfig> = {},
    private onMessage: (msg: any, connectionId: string) => void,
    private getToken: () => string | null,
    private buildUrl: (endpoint: string) => string
  ) {
    this.connectionId = connectionId;
    this.config = {
      endpoint,
      reconnect: config.reconnect ?? true,
      reconnectDelay: config.reconnectDelay ?? 1000,
      maxReconnectAttempts: config.maxReconnectAttempts ?? 30,
      timeout: config.timeout ?? 5000,
    };

    this.state$ = new BehaviorSubject<ConnectionState>({
      id: this.connectionId,
      endpoint: this.config.endpoint,
      connected: false,
      connecting: false,
      error: null,
      reconnectAttempts: 0,
    });
  }

  connect(): Observable<ConnectionState> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return this.state$.asObservable();
    }
    if (this.ws?.readyState === WebSocket.CONNECTING) {
      return this.state$.asObservable();
    }

    this.updateState({ connecting: true, error: null });

    const jwtToken = this.getToken();
    if (!jwtToken) {
      const error = new Error("JWT token not found");
      this.updateState({ connecting: false, error });
      return this.state$.asObservable();
    }

    const fullUrl = this.buildUrl(this.config.endpoint);

    try {
      this.ws = new WebSocket(fullUrl, [jwtToken]);

      this.connectionTimeout = setTimeout(() => {
        if (this.ws?.readyState !== WebSocket.OPEN) {
          this.ws?.close();
          const error = new Error("Connection timeout");
          this.updateState({ connecting: false, error });
        }
      }, this.config.timeout);

      this.ws.onopen = () => {
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }
        this.updateState({
          connected: true,
          connecting: false,
          error: null,
          reconnectAttempts: 0,
        });
      };

      this.ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          this.messageSubject.next(msg);
          this.onMessage(msg, this.connectionId);
        } catch (error) {
          console.error(`[WS ${this.connectionId}] Parse error:`, error);
        }
      };

      this.ws.onerror = (event) => {
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }
        const error =
          event instanceof ErrorEvent
            ? new Error(event.message || "WebSocket error")
            : new Error("WebSocket connection error");
        this.updateState({ connecting: false, error });
      };

      this.ws.onclose = (event) => {
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }

        const wasActive = this.ws !== null;
        this.ws = null;
        this.updateState({ connected: false, connecting: false });

        if (
          this.config.reconnect &&
          !event.wasClean &&
          wasActive &&
          this.state$.value.reconnectAttempts < this.config.maxReconnectAttempts
        ) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      this.updateState({ connecting: false, error: error as Error });
    }

    return this.state$.asObservable();
  }

  private scheduleReconnect() {
    const attempts = this.state$.value.reconnectAttempts + 1;
    const delay = Math.min(
      this.config.reconnectDelay * Math.pow(2, attempts - 1),
      30000
    );
    this.updateState({ reconnectAttempts: attempts });
    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.messageSubject.complete();
    this.state$.complete();
  }

  send(message: any) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn(
        `[WS ${this.connectionId}] Not connected, message not sent:`,
        message
      );
    }
  }

  getMessageStream(): Observable<any> {
    return this.messageSubject.asObservable();
  }

  getState(): Observable<ConnectionState> {
    return this.state$.asObservable();
  }

  isConnected(): boolean {
    return this.state$.value.connected;
  }

  private updateState(updates: Partial<ConnectionState>) {
    this.state$.next({ ...this.state$.value, ...updates });
  }
}
